import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom'; // Adicionado Link
import marketService from '../services/marketService';
import useAuth from '../hooks/useAuth';
import './MarketPlayerDetails.css';

const MarketPlayerDetails = () => {
  const { id: playerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [playerData, setPlayerData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    const fetchPlayerDetails = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(null);
        if (!playerId || !/^[0-9a-fA-F]{24}$/.test(playerId)) {
          throw new Error('Formato de ID do jogador inválido');
        }
        const { success, data, error } = await marketService.getById(playerId);
        if (!success) {
          throw new Error(error || 'Servidor retornou dados inválidos');
        }
        if (!data?.name || !data?.position) {
          throw new Error('Dados essenciais do jogador ausentes');
        }
        setPlayerData(data);
      } catch (error) {
        console.error('Erro ao buscar detalhes do jogador:', error);
        setErrorMessage(getUserFriendlyError(error.message));
        if (error.message.toLowerCase().includes('not found') || error.message.toLowerCase().includes('invalid')) {
          setTimeout(() => navigate('/market'), 2000);
        }
      } finally {
        setIsLoading(false);
      }
    };

    const getUserFriendlyError = (error) => {
      const msg = error.toLowerCase();
      if (msg.includes('not found')) return 'Jogador não encontrado no sistema';
      if (msg.includes('invalid')) return 'Informações inválidas do jogador';
      if (msg.includes('server')) return 'Erro no servidor, por favor tente novamente';
      return 'Falha ao carregar os detalhes do jogador';
    };

    fetchPlayerDetails();
  }, [playerId, navigate]);

  const isOwner = user && playerData && user._id === playerData.user._id;

  if (isLoading) {
    return (
      <div className="player-details-loading">
        <div className="loading-indicator"><div className="spinner"></div></div>
        <p>Carregando informações do jogador...</p>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="player-details-error">
        <h3>Erro ao carregar jogador</h3>
        <p>{errorMessage}</p>
        <div className="error-actions">
          <button onClick={() => window.location.reload()} className="retry-button">
            Tentar novamente
          </button>
          <button onClick={() => navigate('/market')} className="back-button">
            Voltar ao Mercado
          </button>
        </div>
      </div>
    );
  }

  if (!playerData) {
    return null; // O useEffect já redireciona em caso de erro, então não precisa de um estado 'não encontrado' aqui.
  }
  
  // Filtra as fotos para não exibir a foto de perfil na galeria, caso ela esteja duplicada.
  const galleryPhotos = playerData.photos?.filter(p => p !== playerData.profilePhoto) || [];

  return (
    <div className="player-details-container">
      <div className="player-header">
        <div className="player-profile-photo-container">
          <img
            src={playerData.profilePhoto || '/default-profile.jpg'}
            alt={`Foto de perfil de ${playerData.name}`}
            className="player-profile-photo"
            onError={(e) => { e.target.onerror = null; e.target.src = '/default-profile.jpg'; }}
          />
        </div>
        <h1>{playerData.name}</h1>
        <div className="player-position-badge">
          {playerData.position || 'Posição não especificada'}
        </div>
        {isOwner && (
          <Link to={`/market/edit/${playerId}`} className="edit-button-details">
            Editar Perfil
          </Link>
        )}
      </div>

      <div className="player-content">
        <section className="basic-info-section">
          <h2>Informações Básicas</h2>
          <ul className="info-list">
            <li><span className="info-label">Idade:</span> {playerData.age || 'Não especificado'}</li>
            <li><span className="info-label">Localização:</span> {playerData.city || 'Não especificado'}</li>
            {playerData.phone && (<li><span className="info-label">Contato:</span> {playerData.phone}</li>)}
          </ul>
        </section>

        {playerData.experience && (
          <section className="experience-section">
            <h2>Experiência</h2>
            <p className="experience-content">{playerData.experience}</p>
          </section>
        )}

        {playerData.description && (
          <section className="description-section">
            <h2>Sobre o Jogador</h2>
            <p className="description-content">{playerData.description}</p>
          </section>
        )}

        {galleryPhotos.length > 0 && (
          <section className="gallery-section">
            <h2>Galeria de Fotos</h2>
            <div className="photo-grid">
              {galleryPhotos.map((photoUrl, index) => (
                <div key={index} className="photo-item">
                  <img src={photoUrl} alt={`${playerData.name} - Foto ${index + 1}`} className="player-photo" loading="lazy" />
                </div>
              ))}
            </div>
          </section>
        )}

        {playerData.videos?.length > 0 && (
          <section className="media-section">
            <h2>Destaques em Vídeo</h2>
            <div className="video-grid"> {/* Mudei para video-grid para melhor layout */}
              {playerData.videos.map((videoUrl, index) => {
                const isCloudinaryVideo = videoUrl.includes('cloudinary');
                const youtubeIdMatch = !isCloudinaryVideo && videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
                
                if (isCloudinaryVideo) {
                  // --- RENDERIZA VÍDEOS DO CLOUDINARY ---
                  return (
                    <div key={index} className="video-item-container">
                      <video
                        src={videoUrl}
                        controls
                        className="player-video"
                        preload="metadata"
                      >
                        Seu navegador não suporta a tag de vídeo.
                      </video>
                    </div>
                  );
                }
                
                if (youtubeIdMatch) {
                  // --- RENDERIZA VÍDEOS DO YOUTUBE ---
                  const youtubeId = youtubeIdMatch[1];
                  return (
                    <div key={index} className="video-item-container">
                      <iframe
                        src={`https://www.youtube.com/embed/${youtubeId}`}
                        title={`Vídeo ${index + 1}`}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="player-video-iframe"
                      />
                    </div>
                  );
                }
                
                // --- RENDERIZA OUTROS LINKS DE VÍDEO ---
                return (
                  <div key={index} className="video-item-container link-container">
                    <a href={videoUrl} target="_blank" rel="noopener noreferrer" className="video-link">
                      Assistir Vídeo Externo {index + 1}
                    </a>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default MarketPlayerDetails;